import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'validate.js';

import { pushParams, resetParams } from 'dpl/actions/query_params';
import {
  FILTERS_MODAL,
  FOLLOWABLE_ID,
  FOLLOWABLE_TYPE,
  PAGE,
  RESULTS_TYPE
} from 'dpl/constants/query_params';
import {
  FILTER_PARAMS,
  LOCATION_FILTER_PARAMS,
  PUPPY_FILTERS,
  RESULTS_TYPES
} from 'dpl/puppy_search_results/utils/constants';
import { pick } from 'dpl/shared/utils';

const NON_LOCATION_FILTER_PARAMS = FILTER_PARAMS.filter(
  filter => !LOCATION_FILTER_PARAMS.includes(filter)
);

function findChangedParamKeys(params, prevParams) {
  return [
    ...new Set([...Object.keys(prevParams), ...Object.keys(params)])
  ].filter(key => prevParams[key] !== params[key]);
}

export default function useSearchFiltering() {
  const dispatch = useDispatch();
  const params = useSelector(({ queryParams }) => queryParams);
  const prevParamsRef = useRef(params);
  const prevParams = prevParamsRef.current;

  const filters = useMemo(
    () => pick(params, NON_LOCATION_FILTER_PARAMS),
    [params]
  );

  const isFiltersModalOpen = params[FILTERS_MODAL] === 'true';
  const hasFilters = !isEmpty(filters);
  const resultsType = params[RESULTS_TYPE] || RESULTS_TYPES.BREEDERS;

  const puppyResultsFilters = useMemo(
    () => pick(params, PUPPY_FILTERS),
    [params]
  );

  function toggleFilters() {
    dispatch(
      pushParams({ [FILTERS_MODAL]: !isFiltersModalOpen }, { hash: true })
    );
  }

  function clearFilters() {
    const filterNames = [
      PAGE,
      RESULTS_TYPE,
      ...NON_LOCATION_FILTER_PARAMS
    ].filter(Boolean);
    dispatch(resetParams(...filterNames, { hash: true }));
  }

  function clearPuppyFilters() {
    if (isEmpty(puppyResultsFilters)) return;

    dispatch(resetParams(...PUPPY_FILTERS, { hash: true }));
  }

  function setResultsType(newResultsType) {
    if (newResultsType === resultsType) {
      return;
    }

    dispatch(pushParams({ [RESULTS_TYPE]: newResultsType }, { hash: true }));

    if (newResultsType === RESULTS_TYPES.BREEDERS) {
      clearPuppyFilters();
    }
  }

  useEffect(() => {
    const changedParamKeys = findChangedParamKeys(params, prevParams);

    prevParamsRef.current = params;

    if (
      // No query params were changed
      isEmpty(changedParamKeys) ||
      // User manually switched search results tabs
      (changedParamKeys.length === 1 && changedParamKeys[0] === RESULTS_TYPE) ||
      // User is navigating between pages
      (changedParamKeys.length === 1 && changedParamKeys[0] === PAGE) ||
      // User reset location selection
      changedParamKeys.every(param => LOCATION_FILTER_PARAMS.includes(param)) ||
      // User triggered the auth modal (e.g. FavoriteButton click while logged out)
      changedParamKeys.includes('direct_signup') ||
      // PostAuthActionProvider cleared the follow params after auto-following
      changedParamKeys.every(param =>
        [FOLLOWABLE_TYPE, FOLLOWABLE_ID].includes(param)
      )
    ) {
      return;
    }

    if (
      isEmpty(puppyResultsFilters) &&
      params[RESULTS_TYPE] === RESULTS_TYPES.PUPPIES
    ) {
      dispatch(
        pushParams({ [RESULTS_TYPE]: RESULTS_TYPES.BREEDERS }, { hash: true })
      );
    }
  }, [params]);

  return {
    clearFilters,
    hasFilters,
    isFiltersModalOpen,
    resultsType,
    toggleFilters,
    setResultsType
  };
}
